import { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Chip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  AutoStories,
  Lightbulb,
  EmojiObjects,
  Favorite,
  School,
  TrendingUp,
} from "@mui/icons-material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { motion } from "framer-motion";

const eduTheme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 481,
      md: 769,
      lg: 1021,
      xl: 1800,
    },
  },
  palette: {
    primary: { main: "#3B5BDB", light: "#E8EDFF", dark: "#1E3A8A" },
    secondary: { main: "#0F9D8E", light: "#E0F7F5", dark: "#076E63" },
    background: { default: "#F8F9FF", paper: "#FFFFFF" },
    text: { primary: "#1A1D2E", secondary: "#4A4D60" },
  },
  typography: {
    fontFamily: "'Inter', 'Roboto', sans-serif",
    h1: { fontWeight: 700, letterSpacing: "-0.02em" },
    h2: { fontWeight: 700, letterSpacing: "-0.01em" },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
  },
  shape: { borderRadius: 16 },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: "0 2px 12px rgba(59,91,219,0.07)",
          border: "1px solid rgba(59,91,219,0.08)",
        },
      },
    },
  },
});

const pillars = [
  {
    icon: <School sx={{ fontSize: 28 }} />,
    title: "Strong Fundamentals",
    desc: "We build solid foundations that outlast any exam — because real knowledge compounds over time.",
    color: "#3B5BDB",
    bg: "#E8EDFF",
  },
  {
    icon: <Lightbulb sx={{ fontSize: 28 }} />,
    title: "Critical Thinking",
    desc: "We ask 'why' more than 'what', nurturing curious minds that question, explore, and discover.",
    color: "#0F9D8E",
    bg: "#E0F7F5",
  },
  {
    icon: <Favorite sx={{ fontSize: 28 }} />,
    title: "Confidence Building",
    desc: "A safe, warm environment where students feel comfortable asking questions and making mistakes.",
    color: "#D946A8",
    bg: "#FCE8F6",
  },
  {
    icon: <EmojiObjects sx={{ fontSize: 28 }} />,
    title: "Love for Learning",
    desc: "When learning becomes meaningful and joyful, success isn't chased — it follows naturally.",
    color: "#F59E0B",
    bg: "#FEF3C7",
  },
  {
    icon: <AutoStories sx={{ fontSize: 28 }} />,
    title: "Creativity & Curiosity",
    desc: "Independent thinking and creative exploration are at the heart of every lesson we design.",
    color: "#7C3AED",
    bg: "#EDE9FE",
  },
  {
    icon: <TrendingUp sx={{ fontSize: 28 }} />,
    title: "Future-Ready",
    desc: "Preparing students not just for exams, but for the opportunities and challenges ahead.",
    color: "#059669",
    bg: "#D1FAE5",
  },
];

const stats = [
  { value: "100%", label: "Student-first approach" },
  { value: "∞", label: "Curiosity encouraged" },
  { value: "1:1", label: "Personalised attention" },
];

// Motion variants
const heroTextVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: (custom = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut", delay: custom },
  }),
};

const statsContainerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.5,
    },
  },
};

const statItemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: "easeOut" },
  },
};

const pillarsContainerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const pillarCardVariants = {
  hidden: { opacity: 0, y: 40, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.5, ease: "easeOut" },
  },
};

const MotionBox = motion(Box);
const MotionTypography = motion(Typography);
const MotionChip = motion(Chip);
const MotionCard = motion(Card);

export default function LearningPhilosophy() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [hovered, setHovered] = useState(null);

  return (
    <ThemeProvider theme={eduTheme}>
      <Box sx={{ bgcolor: "background.default", minHeight: "100vh", pb: 8 }}>
        {/* Hero */}
        <Box
          sx={{
            background:
              "linear-gradient(135deg, #3B5BDB 0%, #1E3A8A 60%, #0F9D8E 100%)",
            pt: { xs: 8, md: 12 },
            pb: { xs: 10, md: 14 },
            px: { xs: 1.5, sm: 2, md: 3 },
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Decorative circles */}
          {[160, 300, 80].map((size, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{
                opacity: 1,
                scale: 1,
                rotate: [0, 8, 0],
              }}
              transition={{
                opacity: { duration: 0.8, delay: i * 0.15 },
                scale: { duration: 0.8, delay: i * 0.15 },
                rotate: {
                  duration: 8 + i * 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                },
              }}
              style={{
                position: "absolute",
                width: size,
                height: size,
                borderRadius: "50%",
                border: "1.5px solid rgba(255,255,255,0.1)",
                top: ["-40px", "20%", "60%"][i],
                right: ["-60px", "-80px", "10%"][i],
                pointerEvents: "none",
              }}
            />
          ))}

          <Container maxWidth={false} sx={{ position: "relative", zIndex: 1,     maxWidth: "1400px", }}>
            <MotionChip
              label="Our Learning Philosophy"
              initial="hidden"
              animate="visible"
              custom={0}
              variants={heroTextVariants}
              sx={{
                bgcolor: "rgba(255,255,255,0.15)",
                color: "#fff",
                fontWeight: 600,
                fontSize: 12,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                mb: 3,
                border: "1px solid rgba(255,255,255,0.2)",
              }}
            />
            <MotionTypography
              variant={isMobile ? "h3" : "h2"}
              initial="hidden"
              animate="visible"
              custom={0.1}
              variants={heroTextVariants}
              sx={{ color: "#fff", fontWeight: 800, mb: 2.5, lineHeight: 1.2 }}
            >
              Learning Beyond
              <br />
              <Box component="span" sx={{ color: "#A5F3FC" }}>
                Textbooks
              </Box>
            </MotionTypography>
            <MotionTypography
              variant="h6"
              initial="hidden"
              animate="visible"
              custom={0.2}
              variants={heroTextVariants}
              sx={{
                color: "rgba(255,255,255,0.8)",
                fontWeight: 400,
                maxWidth: 560,
                lineHeight: 1.7,
              }}
            >
              We believe true education goes beyond memorising lessons. Every
              child has unique talents and the ability to achieve great things —
              when guided with patience and the right support.
            </MotionTypography>

            {/* Stats */}
            <MotionBox
              initial="hidden"
              animate="visible"
              variants={statsContainerVariants}
              sx={{
                display: "flex",
                gap: { xs: 2, sm: 3, md: 6 },
                mt: 5,
                flexWrap: "wrap",
              }}
            >
              {stats.map((s) => (
                <MotionBox key={s.label} variants={statItemVariants}>
                  <Typography
                    sx={{
                      color: "#fff",
                      fontWeight: 800,
                      fontSize: { xs: 24, sm: 28, md: 36 },
                      lineHeight: 1,
                    }}
                  >
                    {s.value}
                  </Typography>
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.65)",
                      fontSize: { xs: 12, sm: 13 },
                      mt: 0.5,
                    }}
                  >
                    {s.label}
                  </Typography>
                </MotionBox>
              ))}
            </MotionBox>
          </Container>
        </Box>

        {/* Pillars */}
        <Container
          maxWidth={false}
          sx={{
            mt: -5,
            position: "relative",
            zIndex: 2,
            px: { xs: 1, md: 3 },
            containerType: "inline-size",
          }}
        >
          <MotionBox
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            variants={pillarsContainerVariants}
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr",
              "@container (min-width:449px)": {
                gridTemplateColumns: "repeat(2, 1fr)",
              },
              "@container (min-width:769px)": {
                gridTemplateColumns: "repeat(3, 1fr)",
              },
              "@container (min-width:1021px)": {
                gridTemplateColumns: "repeat(6, 1fr)",
              },
              gap: { xs: 1.5, sm: 2 },
              pb: 2,
            }}
          >
            {pillars.map((p, i) => (
              <MotionCard
                key={p.title}
                variants={pillarCardVariants}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
                whileHover={{
                  y: -6,
                  boxShadow: "0 8px 28px rgba(59,91,219,0.14)",
                }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                sx={{
                  width: "100%",
                  height: "100%",
                  borderRadius: 3,
                  cursor: "default",
                  "@container (max-width:448px)": {
                    width: "78%",
                    justifySelf: "center",
                      borderRadius: 1.5,
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 2.25, sm: 3 }, "&:last-child": { pb: { xs: 2.25, sm: 3 } } }}>
                  <motion.div
                    animate={{ scale: hovered === i ? 1.12 : 1 }}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                    style={{
                      width: 52,
                      height: 52,
                      borderRadius: 8,
                      backgroundColor: p.bg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: 16,
                      color: p.color,
                    }}
                  >
                    {p.icon}
                  </motion.div>

                  <Typography
                    sx={{
                      fontSize: "1rem", // 16px
                      fontWeight: 700,
                      color: "text.primary",
                      mb: 1.5,
                      lineHeight: 1.4,
                    }}
                  >
                    {p.title}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: "0.92rem", // ~15px
                      color: "text.secondary",
                      lineHeight: 1.8,
                    }}
                  >
                    {p.desc}
                  </Typography>
                </CardContent>
              </MotionCard>
            ))}
          </MotionBox>
        </Container>
      </Box>
    </ThemeProvider>
  );
}